import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';
import { collection, DocumentSnapshot, getDocs, query, where } from 'firebase/firestore';
import { firestoreInstance } from '../../../config/ApiService';
import { AGREEMENT_TYPE } from 'components/ClinicalServicesDropdown';

const useStyles = makeStyles((theme: Theme) => ({
    formControl: {
        width: 200
    },
    textField: {
        marginBottom: theme.spacing(2)
    }
}));

interface Props {
    onCustomerSelected: (param: { customerId: string; customerName: string }) => void;
}

const CustomerSelect = (props: Props) => {
    const { onCustomerSelected } = props;
    const [customers, setCustomers] = React.useState<Array<{ name: string; id: string }>>([]);
    const [selected, setSelected] = React.useState('');
    const classes = useStyles();

    const handleChange = (evt: SelectChangeEvent) => {
        const customerId = evt.target.value as string;
        setSelected(customerId);
        if (onCustomerSelected) {
            const selectedCustomer = customers.find((customer) => customer.id === customerId);
            onCustomerSelected({ customerId, customerName: selectedCustomer?.name || '' });
        }
    };

    React.useEffect(() => {
        let asyncCallInProgress = true;
        (async () => {
            const tempArr: Array<{ name: string; id: string }> = [];
            const ref = collection(firestoreInstance(), 'clients');
            const q = query(
                ref,
                where('agreement_type', 'in', [
                    AGREEMENT_TYPE.ENGAGE,
                    AGREEMENT_TYPE.ACCESS_AND_ENGAGE,
                    AGREEMENT_TYPE.ACCESS_ENGAGE_AND_COLLECT
                ]),
                where('clinical_services_enabled', '==', true)
            );
            const snapshots = await getDocs(q);
            snapshots.forEach((doc: DocumentSnapshot) => {
                const data = doc.data() || { name: '', id: '' };
                tempArr.push({ ...doc.data(), name: data.name, id: doc.id });
            });
            if (asyncCallInProgress) {
                setCustomers(tempArr);
            }
        })();
        return () => {
            asyncCallInProgress = false;
        };
    }, []);

    return (
        <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel htmlFor="customer-select">Customer</InputLabel>
            <Select
                variant="outlined"
                label="Customer"
                id="customer-select"
                value={selected}
                onChange={handleChange}
            >
                {customers
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((customer) => (
                        <MenuItem value={customer.id} key={customer.id}>
                            {customer.name}
                        </MenuItem>
                    ))}
            </Select>
        </FormControl>
    );
};

export default CustomerSelect;
